import { takeLatest, put, call } from 'redux-saga/effects';
import axiosInstance from 'clients/api';
import { setError } from '../error/slice';
import {
    LIVE_CHAT_CONVERSATIONS_CALL,
    LIVE_CHAT_CREATE_CONVERSATION_CALL,
    LIVE_CHAT_MESSAGES_CALL,
    LIVE_CHAT_SEND_MESSAGE_CALL,
    LIVE_CHAT_UPDATE_STATUS_CALL
} from './sagas-actions';
import {
    onCallConversations,
    onSuccessConversations,
    onFailedConversations,
    onCallCreateConversation,
    onSuccessCreateConversation,
    onFailedCreateConversation,
    onCallMessages,
    onSuccessMessages,
    onFailedMessages,
    onCallSendMessage,
    onSuccessSendMessage,
    onFailedSendMessage,
    onSuccessUpdateStatus
} from './slice';

const prefix = '/tenantUser/chat';

export function* conversationsCallGenerator(action) {
    yield put(onCallConversations());
    try {
        const params = action.payload?.status ? { status: action.payload.status } : {};
        const { data } = yield call(axiosInstance.get, `${prefix}/conversation`, { params });
        yield put(onSuccessConversations(data));
    } catch (error) {
        yield put(setError({ error, action }));
        yield put(onFailedConversations());
    }
}

export function* createConversationCallGenerator(action) {
    yield put(onCallCreateConversation());
    try {
        const { data } = yield call(axiosInstance.post, `${prefix}/conversation`);
        yield put(onSuccessCreateConversation(data));
    } catch (error) {
        yield put(setError({ error, action }));
        yield put(onFailedCreateConversation());
    }
}

export function* messagesCallGenerator(action) {
    const { conversationId, limit = 50, offset = 0 } = action.payload;
    yield put(onCallMessages({ offset }));
    try {
        const { data } = yield call(
            axiosInstance.get,
            `${prefix}/conversation/${conversationId}/message`,
            { params: { limit, offset } }
        );
        yield put(onSuccessMessages({ messages: data, offset }));
    } catch (error) {
        yield put(setError({ error, action }));
        yield put(onFailedMessages());
    }
}

export function* sendMessageCallGenerator(action) {
    yield put(onCallSendMessage());
    try {
        const { conversationId, body, attachments } = action.payload;
        let payload;

        if (attachments?.length) {
            payload = new FormData();
            payload.append('body', body || '');
            attachments.forEach((file) => payload.append('attachments', file, file.name));
        } else {
            payload = { body };
        }

        const { data } = yield call(
            axiosInstance.post,
            `${prefix}/conversation/${conversationId}/message`,
            payload
        );
        yield put(onSuccessSendMessage(data));
    } catch (error) {
        // BE returns 400 when posting to a non-open conversation. Flip status
        // locally so the composer swaps to the "Start new chat" banner even
        // if the LIVE_CHAT_STATUS_CHANGED socket event was missed.
        if (error?.response?.status === 400) {
            yield put(onSuccessUpdateStatus({
                conversationId: action.payload.conversationId,
                status: 'resolved'
            }));
        }
        yield put(setError({ error, action }));
        yield put(onFailedSendMessage());
    }
}

export function* updateStatusCallGenerator(action) {
    try {
        const { conversationId, status } = action.payload;
        yield call(
            axiosInstance.put,
            `${prefix}/conversation/${conversationId}/status`,
            { status }
        );
        yield put(onSuccessUpdateStatus({ conversationId, status }));
    } catch (error) {
        yield put(setError({ error, action }));
    }
}

export default function* liveChatWatcherSaga() {
    yield takeLatest(LIVE_CHAT_CONVERSATIONS_CALL, conversationsCallGenerator);
    yield takeLatest(LIVE_CHAT_CREATE_CONVERSATION_CALL, createConversationCallGenerator);
    yield takeLatest(LIVE_CHAT_MESSAGES_CALL, messagesCallGenerator);
    yield takeLatest(LIVE_CHAT_SEND_MESSAGE_CALL, sendMessageCallGenerator);
    yield takeLatest(LIVE_CHAT_UPDATE_STATUS_CALL, updateStatusCallGenerator);
}
